import { get, save } from "@/lib/utils";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "react-toastify";

import { analytics, EventEnum } from "@/lib/analytics";
import { getErrorMessage } from "@/lib/lutely-api/error-manager";
import { useCheckoutControllerCreateCartCheckoutSession } from "@/lib/lutely-api/gen/checkout/checkout";
import {
  DetailAlbumDto,
  DetailTrackDto,
  LicenseConfigurationDtoLicenseType,
} from "@/lib/lutely-api/gen/dtos";

// Track cart item - requires license configuration
export type TrackCartItem = {
  type: "track";
  trackId: string;
  priceId: string;
  licenseType: LicenseConfigurationDtoLicenseType;
  price: number;
  track: DetailTrackDto;
};

// Album cart item - flat price or pay-what-you-want
export type AlbumCartItem = {
  type: "album";
  albumId: string;
  price: number;
  priceId?: string;
  album: DetailAlbumDto;
  chosenPrice?: number;
};

export type CartItemType = TrackCartItem | AlbumCartItem;

// Type guards for cart item discrimination
export function isTrackCartItem(item: CartItemType): item is TrackCartItem {
  return item.type === "track";
}

export function isAlbumCartItem(item: CartItemType): item is AlbumCartItem {
  return item.type === "album";
}

// Helper to get unique ID for any cart item
export function getCartItemId(item: CartItemType): string {
  return isTrackCartItem(item) ? item.priceId : item.albumId;
}

/** Cart as localStorage holds it right now, with legacy (pre-album) items
 * normalized to carry the type discriminator. */
function readStoredCart(): CartItemType[] {
  const storedCart = get("lutely-cart", "array") as unknown[];
  return storedCart
    .filter(
      (item): item is Record<string, unknown> =>
        item != null && typeof item === "object",
    )
    .map((item) => {
      if (!("type" in item)) {
        return { ...item, type: "track" } as TrackCartItem;
      }
      return item as CartItemType;
    });
}

function getCheckoutSuccessUrl(
  cartItems: CartItemType[],
  subtotalPrice: number,
): string {
  const defaultSuccessUrl =
    subtotalPrice === 0
      ? `${window.location.origin}/account/purchases`
      : `${window.location.origin}/account/purchases?session_id={CHECKOUT_SESSION_ID}`;

  const item = cartItems[0];
  if (cartItems.length !== 1 || !isAlbumCartItem(item)) {
    return defaultSuccessUrl;
  }

  // Single-album purchase: send the buyer back to the album's page regardless
  // of where checkout was started. Prefer the branded slug URL
  // (/@creator/album), falling back to the ID-based route when slugs are
  // unavailable. This must match the routes the album page is served from
  // (see AlbumLink) so fulfillment runs on the destination page.
  const { album, albumId } = item;
  const albumPath =
    album.creator.slug && album.slug
      ? `/@${album.creator.slug}/${album.slug}`
      : `/albums/${albumId}`;

  const successUrl = `${window.location.origin}${albumPath}`;
  return subtotalPrice === 0
    ? successUrl
    : `${successUrl}?session_id={CHECKOUT_SESSION_ID}`;
}

interface CartManagerContextType {
  isCartOpen: boolean;
  totalCartItems: number;
  subtotalPrice: number;
  cartItems: CartItemType[];
  addToCart: (item: CartItemType) => void;
  removeFromCart: (id: string) => void;
  onItemExists: (id: string) => boolean;
  onToggleCart: () => void;
  checkoutSession: ReturnType<
    typeof useCheckoutControllerCreateCartCheckoutSession
  >;
  checkoutCart: (options?: { shareContact?: boolean }) => void;
  clearCart: () => void;
}

export const CartManagerContext = createContext<CartManagerContextType | null>(
  null,
);

interface CartManagerProviderProps {
  children: ReactNode;
}

const CartManagerProvider = ({ children }: CartManagerProviderProps) => {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartItems, setCartItems] = useState<CartItemType[]>([]);
  // const location = useLocation();
  // const router = useRouter();

  const checkoutSession = useCheckoutControllerCreateCartCheckoutSession();
  const searchParams = useSearch({ strict: false });
  const navigate = useNavigate({});
  const openCartQueryParam = searchParams["open-cart"];

  // Open cart if "open-cart" search param is present
  useEffect(() => {
    setIsCartOpen(openCartQueryParam === "true");
  }, [openCartQueryParam]);

  const openCart = useCallback(() => {
    navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        "open-cart": "true",
      }),
    });
  }, [navigate]);

  const closeCart = useCallback(() => {
    navigate({
      to: ".",
      search: (prev) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { "open-cart": _, ...rest } = prev;
        return rest;
      },
    });
  }, [navigate]);

  const onToggleCart = useCallback(() => {
    if (openCartQueryParam === "true") {
      closeCart();
    } else {
      openCart();
    }
  }, [openCartQueryParam, openCart, closeCart]);

  useEffect(() => {
    setCartItems(readStoredCart());
  }, []);

  // Another tab changed the cart: adopt its state instead of clobbering it
  // with this tab's stale copy on the next add/remove (LUT-944).
  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key !== "lutely-cart") return;
      setCartItems(readStoredCart());
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const subtotalPrice = cartItems.reduce(
    (acc, b) => acc + (Number(b.price) || 0),
    0,
  );

  const checkoutCart = useCallback(
    async (options?: { shareContact?: boolean }) => {
      try {
        analytics.track(EventEnum.CHECKOUT_STARTED, {
          cartItems: cartItems,
          subtotal: subtotalPrice,
        });

        const response = await checkoutSession.mutateAsync({
          data: {
            successUrl: getCheckoutSuccessUrl(cartItems, subtotalPrice),
            cancelUrl: window.location.href,
            shareContact: options?.shareContact,
            cartItems: cartItems.map((item) => {
              if (isTrackCartItem(item)) {
                return {
                  type: "track",
                  trackId: item.trackId,
                  priceId: item.priceId,
                  licenseType: item.licenseType,
                };
              } else {
                return {
                  type: "album",
                  albumId: item.albumId,
                  chosenPrice: item.chosenPrice ?? item.price,
                };
              }
            }),
          },
        });

        closeCart();
        const redirectUrl = response.url;
        window.location.href = redirectUrl;
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    },
    [checkoutSession, cartItems, subtotalPrice],
  );

  const addToCart = useCallback(
    (item: CartItemType) => {
      openCart();

      // Mutate on top of what storage actually holds — this tab's in-memory
      // copy can be stale when another tab added items after it mounted, and
      // writing the stale copy back silently drops theirs (LUT-944).
      const stored = readStoredCart().filter(
        (existing) => getCartItemId(existing) !== getCartItemId(item),
      );
      const updatedCart = [...stored, item];
      setCartItems(updatedCart);
      save("lutely-cart", updatedCart);
    },
    [openCart],
  );

  const removeFromCart = useCallback((id: string) => {
    const updatedCart = readStoredCart().filter(
      (item) => getCartItemId(item) !== id,
    );
    setCartItems(updatedCart);
    save("lutely-cart", updatedCart);
  }, []);

  const clearCart = useCallback(() => {
    save("lutely-cart", []);
    setCartItems([]);
  }, []);

  const onItemExists = useCallback(
    (id: string) => cartItems.some((item) => getCartItemId(item) === id),
    [cartItems],
  );

  const value = useMemo(
    () => ({
      isCartOpen,
      totalCartItems: cartItems.length,
      subtotalPrice,
      cartItems,
      addToCart,
      removeFromCart,
      onItemExists,
      onToggleCart,
      checkoutSession,
      checkoutCart,
      clearCart,
    }),
    [
      isCartOpen,
      cartItems,
      subtotalPrice,
      checkoutSession,
      addToCart,
      checkoutCart,
      onItemExists,
      removeFromCart,
      clearCart,
    ],
  );

  return (
    <CartManagerContext.Provider value={value}>
      {children}
    </CartManagerContext.Provider>
  );
};

export const useCartManager = () => {
  const context = useContext(CartManagerContext);
  if (!context) {
    throw new Error("useCartManager must be used within a CartManagerProvider");
  }
  return context;
};

export default CartManagerProvider;
